import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useGateAccess } from '../hooks/useGateAccess';
import DigitalAssetPurchase from './DigitalAssetPurchase';
import ComponentControls from "./ComponentControls";
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import ImageModal from "./ImageModal";
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { auth } from '../firebaseConfig';
import { buildPanelClassName } from '../utils/panelTheme';
import { normalizeImageGallery } from '../utils/imageGallery';
import { queueSocialFeedShareDraft } from '../utils/socialFeedShareDraft';

export default function ImageComponent({
                                           comp,
                                           index,
                                           moveUp,
                                           moveDown,
                                           onEdit,
                                           isEditor = false,
                                           removeComponent,
                                           donationCampaignOptions = [],
                                           donationGate,
                                           onDonationGateChange,
                                           onDirectPaymentGateChange,
                                           onDirectPaymentDraftChange,
                                           gateType,
                                           selectedListId,
                                           onGateTypeChange,
                                           onGateListChange,
                                           extraGates,
                                           gateLogic,
                                           onExtraGatesChange,
                                           onGateLogicChange,
                                           style = {},
                                           onStyleChange,
                                           coverUrl = '',
                                           onCoverChange,
                                           onPanelBackgroundImageChange,
                                           timestampDisplay = 'none',
                                           onTimestampDisplayChange,
                                           displayMode,
                                           ownerUsername = '',
                                           pageTypography = null,
                                           ownerPublicKey = '',
                                       }) {
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || { type: 'public', listId: '' });
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );

    const donationMap = useMemo(() => {
        const m = new Map();
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [gateRefreshKey, setGateRefreshKey] = useState(0);
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const [hasAccess, isLoading, gateNamesLabel, , gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;

    const navigate = useNavigate();
    const [viewerUid, setViewerUid] = useState(() => String(auth?.currentUser?.uid || ''));
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setViewerUid(String(u?.uid || ''));
        });
        return () => unsub?.();
    }, []);
    const { username: routeUsername } = useParams();
    const safeUsername = routeUsername || ownerUsername || comp?.muultiName || '';
    const detailHref =
        safeUsername && comp?.id
            ? `/${safeUsername}/i/${comp.id}`
            : null;

    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);

    const mode = displayMode || comp?.displayMode || 'compressed';
    const modeIsFull = mode === 'full';
    const canLinkToDetail = !!detailHref && mode !== 'full';
    const galleryImages = useMemo(
        () => normalizeImageGallery(comp, { alt: comp?.alt, caption: comp?.caption, nsfa: comp?.nsfa }),
        [comp]
    );
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [modalImageIndex, setModalImageIndex] = useState(null);
    const [detailLayout, setDetailLayout] = useState('grid');
    const activeImage = galleryImages[activeImageIndex] || galleryImages[0] || null;
    const modalImage = modalImageIndex == null ? null : (galleryImages[modalImageIndex] || null);

    const revealedKey = `nsfa_revealed_${comp.id}`;
    const [revealed, setRevealed] = useState(() => {
        try { return sessionStorage.getItem(revealedKey) === '1'; } catch { return false; }
    });

    useEffect(() => {
        setActiveImageIndex(0);
        setModalImageIndex(null);
        setDetailLayout('grid');
    }, [comp?.id]);

    useEffect(() => {
        if (activeImageIndex < galleryImages.length) return;
        setActiveImageIndex(Math.max(0, galleryImages.length - 1));
    }, [activeImageIndex, galleryImages.length]);

    const handleOpenModal = (nextIndex = activeImageIndex) => {
        const nextImage = galleryImages[nextIndex];
        if (!nextImage?.url) return;
        if ((nextImage.nsfa || comp.nsfa) && !revealed) return;
        setModalImageIndex(nextIndex);
    };

    const handleCloseModal = () => setModalImageIndex(null);

    const revealNow = () => {
        setRevealed(true);
        try { sessionStorage.setItem(revealedKey, '1'); } catch {}
    };

    const goToPreviousImage = () => {
        if (galleryImages.length <= 1) return;
        setActiveImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    const goToNextImage = () => {
        if (galleryImages.length <= 1) return;
        setActiveImageIndex((prev) => (prev + 1) % galleryImages.length);
    };

    const typo = resolveTypography(pageTypography, style);
    const numToPx = (v) => (typeof v === 'number' ? `${v}px` : (v || undefined));
    const detailLinkStyle = {
        fontFamily: typo.links.fontFamily,
        fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
        fontWeight: typo.links.fontWeight,
        color: typo.links.color,
        textDecoration: typo.links.textDecoration || 'underline',
        lineHeight: typo.links.lineHeight || 'normal',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
    };
    const shareGateInput = useMemo(() => {
        const mapped = (gates || [])
            .map((g) => {
                const type = String(g?.type || 'public');
                if (type === 'public') return { type: 'public' };
                if (type === 'donation') {
                    return {
                        type: 'donation',
                        donationCompId: String(g?.donationCompId || ''),
                        donationThreshold: Number(g?.donationThreshold || 0),
                        campaignName: String(g?.campaignName || ''),
                        currency: String(g?.currency || ''),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'directPayment') {
                    return {
                        type: 'directPayment',
                        priceSol: Number(g?.priceSol || 0),
                        payToUid: String(g?.payToUid || ownerPublicKeyStr || ''),
                        targetId: String(g?.targetId || comp?.id || ''),
                        currency: String(g?.currency || 'SOL'),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'tokensHeld') {
                    return {
                        type: 'tokensHeld',
                        tokenThreshold: Number(g?.tokenThreshold || 0),
                        currency: String(g?.currency || 'SOL'),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'token' || type === 'collection' || type === 'compressedCollection') {
                    return {
                        type,
                        listId: String(g?.listId || ''),
                    };
                }
                return null;
            })
            .filter(Boolean);
        return {
            logic: logic === 'all' ? 'all' : 'any',
            gates: mapped.length ? mapped : [{ type: 'public' }],
        };
    }, [gates, logic, ownerPublicKeyStr, comp?.id]);
    const canShareToSocialFeed = Boolean(detailHref && viewerUid);
    const showInlineShareButton = canShareToSocialFeed;
    const handleShareToSocialFeed = () => {
        if (!canShareToSocialFeed || !detailHref) return;

        const shareUrl = typeof window !== 'undefined'
            ? `${window.location.origin}${detailHref}`
            : detailHref;
        const title = String(comp?.alt || '').trim();
        const summary = String(comp?.caption || '').trim();
        const contentParts = [title, summary, shareUrl].filter(Boolean);

        queueSocialFeedShareDraft({
            source: 'image',
            detailPath: detailHref,
            content: contentParts.join('\n\n'),
            summary,
            tags: ['image'],
            targetId: String(comp?.id || ''),
            gateInput: shareGateInput,
        });
        navigate('/posts');
    };

    const directPaymentGate =
        (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
    const passwordGate =
        (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
    const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
    const rootClass = buildPanelClassName({ style, extra: !isLoading && !hasAccess ? "locked" : "" });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    const renderImageItem = (item, idx, inGrid = false) => {
        if (!item?.url) return null;
        const isBlurred = !!(item.nsfa || comp.nsfa) && !revealed;
        return (
            <div
                key={`${item.url}-${idx}`}
                className={inGrid ? 'image-gallery-grid-item' : 'image-gallery-stage'}
                style={{ position: 'relative', width: '100%' }}
            >
                <img
                    src={item.url}
                    alt={item.alt || comp.alt || ''}
                    onClick={() => handleOpenModal(idx)}
                    style={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: 8,
                        cursor: isBlurred ? 'default' : 'zoom-in',
                        filter: isBlurred ? 'blur(18px)' : 'none',
                        transition: 'filter .2s ease',
                        objectFit: 'cover',
                    }}
                />
                {isBlurred && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: 8,
                            background: 'rgba(0,0,0,0.35)',
                            backdropFilter: 'saturate(70%)',
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#fff', padding: '1rem' }}>
                            <div style={{ fontWeight: 700, marginBottom: 8 }}>Mature content</div>
                            <button
                                onClick={revealNow}
                                className="muulti-button"
                                aria-label="Reveal NSFA image"
                                type="button"
                            >
                                Click to reveal
                            </button>
                        </div>
                    </div>
                )}
                {inGrid && item.caption && (
                    <p
                        className="image-caption"
                        style={{
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight,
                            marginTop: 8
                        }}
                    >
                        {item.caption}
                    </p>
                )}
            </div>
        );
    };

    const renderCarousel = () => {
        if (!activeImage?.url) return null;
        return (
            <div className="image-gallery-carousel">
                <div className="image-gallery-stage-wrap">
                    {renderImageItem(activeImage, activeImageIndex)}
                    {galleryImages.length > 1 && (
                        <>
                            <button
                                type="button"
                                className="image-gallery-nav image-gallery-nav-left"
                                onClick={goToPreviousImage}
                                aria-label="Previous image"
                            >
                                {'<'}
                            </button>
                            <button
                                type="button"
                                className="image-gallery-nav image-gallery-nav-right"
                                onClick={goToNextImage}
                                aria-label="Next image"
                            >
                                {'>'}
                            </button>
                        </>
                    )}
                </div>
                {galleryImages.length > 1 && (
                    <div className="image-gallery-indicators" aria-label="Image selector">
                        {galleryImages.map((_, idx) => (
                            <button
                                key={`dot-${idx}`}
                                type="button"
                                className={`image-gallery-dot${idx === activeImageIndex ? ' is-active' : ''}`}
                                onClick={() => setActiveImageIndex(idx)}
                                aria-label={`Go to image ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderGalleryBody = () => {
        if (galleryImages.length === 0) return null;
        if (!modeIsFull) return renderCarousel();
        if (detailLayout === 'carousel') return renderCarousel();
        return (
            <div className="image-gallery-grid">
                {galleryImages.map((item, idx) => renderImageItem(item, idx, true))}
            </div>
        );
    };

    const activeCaption = (() => {
        if (modeIsFull && detailLayout === 'grid') return '';
        return activeImage?.caption || comp?.caption || '';
    })();

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={rootClass} style={style}>
            {isLoading ? (
                <div className="panel-inner">
                    <p
                        style={{
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight
                        }}
                    >
                        Checking access for {gateNamesLabel}...
                    </p>
                    <TimestampBadge
                        mode={comp.timestampDisplay || 'none'}
                        createdAt={comp.createdAt}
                        updatedAt={comp.updatedAt}
                    />
                </div>
            ) : !hasAccess ? (
                <div className="panel-inner">
                    <h1
                        style={{
                            fontFamily: typo.headers.fontFamily,
                            fontSize: numToPx(typo.headers.fontSize),
                            fontWeight: typo.headers.fontWeight,
                            color: typo.headers.color,
                            lineHeight: typo.headers.lineHeight
                        }}
                    >
                        {deniedHeading} - {gateNamesLabel}
                    </h1>

                    {passwordGate ? (
                        <PasswordUnlock
                            gate={passwordGate}
                            componentId={comp.id}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                    {directPaymentGate ? (
                        <DirectPaymentUnlock
                            gate={directPaymentGate}
                            componentId={comp.id}
                            componentType={comp.type}
                            ownerPublicKey={ownerPublicKeyStr}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                    {donationGateLinks.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            {donationGateLinks.map((d) => (
                                <div key={d.id} style={{ marginTop: 4 }}>
                                    <Link to={d.href} className="detail-page-return-link">
                                        View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                    <TimestampBadge
                        mode={comp.timestampDisplay || 'none'}
                        createdAt={comp.createdAt}
                        updatedAt={comp.updatedAt}
                    />
                    <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                        compId={comp.id}
                        index={index}
                        moveUp={moveUp}
                        moveDown={moveDown}
                        onEdit={onEdit}
                        removeComponent={removeComponent}
                        gateType={gate.type}
                        selectedListId={gate.listId}
                        onGateTypeChange={onGateTypeChange}
                        onGateListChange={onGateListChange}
                        extraGates={extraGates ?? comp.extraGates ?? []}
                        gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                        onExtraGatesChange={onExtraGatesChange}
                        onGateLogicChange={onGateLogicChange}
                        donationCampaignOptions={donationCampaignOptions}
                        donationGate={donationGate ?? comp.gate}
                        onDonationGateChange={onDonationGateChange}
                        onDirectPaymentGateChange={onDirectPaymentGateChange}
                        onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                        style={style}
                        onStyleChange={onStyleChange}
                        coverUrl={coverUrl}
                        onCoverChange={onCoverChange}
                        onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                        timestampDisplay={comp.timestampDisplay || 'none'}
                        onTimestampDisplayChange={(val) => onTimestampDisplayChange?.(val)}
                    />
                </div>
            ) : (
                <div className="panel-inner">
                    {comp?.alt && (
                        <h1
                            style={{
                                fontFamily: typo.headers.fontFamily,
                                fontSize: numToPx(typo.headers.fontSize),
                                fontWeight: typo.headers.fontWeight,
                                color: typo.headers.color,
                                lineHeight: typo.headers.lineHeight
                            }}
                        >
                            {comp.alt}
                        </h1>
                    )}

                    {modeIsFull && galleryImages.length > 1 && (
                        <div className="image-gallery-layout-controls">
                            <button
                                type="button"
                                className={`muulti-button${detailLayout === 'grid' ? ' active' : ''}`}
                                onClick={() => setDetailLayout('grid')}
                            >
                                Grid
                            </button>
                            <button
                                type="button"
                                className={`muulti-button${detailLayout === 'carousel' ? ' active' : ''}`}
                                onClick={() => setDetailLayout('carousel')}
                            >
                                Carousel
                            </button>
                        </div>
                    )}

                    {galleryImages.length > 0 ? (
                        renderGalleryBody()
                    ) : (
                        <p
                            style={{
                                fontFamily: typo.paragraph.fontFamily,
                                fontSize: numToPx(typo.paragraph.fontSize),
                                color: typo.paragraph.color,
                                lineHeight: typo.paragraph.lineHeight,
                            }}
                        >
                            No image available.
                        </p>
                    )}

                    {activeCaption && (
                        <p
                            className="image-caption"
                            style={{
                                fontFamily: typo.paragraph.fontFamily,
                                fontSize: numToPx(typo.paragraph.fontSize),
                                color: typo.paragraph.color,
                                lineHeight: typo.paragraph.lineHeight,
                                marginTop: 8
                            }}
                        >
                            {activeCaption}
                        </p>
                    )}

                    {canLinkToDetail ? (
                        <div style={{ marginTop: 8 }}>
                            <Link to={detailHref} style={detailLinkStyle}>
                                View More ->
                            </Link>
                        </div>
                    ) : null}

                    {modalImage && (
                        <ImageModal
                            image={modalImage.url}
                            name={modalImage.alt || comp.alt || 'image'}
                            onClose={handleCloseModal}
                        />
                    )}
                </div>
            )}

            {hasAccess && (
                <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {showInlineShareButton && (
                <div style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        className="muulti-button"
                        onClick={handleShareToSocialFeed}
                    >
                        Share to Social Feed
                    </button>
                </div>
            )}
            {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}
            <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={(val) => onTimestampDisplayChange?.(val)}
            />
        </div>
        </GateRevealPanel>
    );
}
